<script setup lang="ts">
type Emits = {
  'set-current-step': [number];
};

const emit = defineEmits<Emits>();

const { t } = useTexts();
const { isSocialAuthEnabled } = useFeatureFlagsNew();
const { openToaster } = useLayoutStore();

const {
  isAuthV2FlowEnabled,
  multiStepPreRegisterTitle,
  multiStepPreRegisterSubtitle,
} = useAuth();

const isLoadingRegisterPage = ref<boolean>(false);
const activeRegisterWithPix = ref<boolean>(false);
const socialAuthFirst = ref<boolean>(true);

function handleEmailRegister() {
  emit('set-current-step', 1);
}

function handlePixRegister() {
  openToaster({
    message: t('general.soon'),
    type: 'error',
  });
}
</script>

<template>
  <section>
    <BaseLoading
      class="!bg-auth-bg-primary"
      :icon-loader="true"
      :show="isLoadingRegisterPage"
    />

    <div :class="$style.boxCenter">
      <div :class="$style.registerContainer">
        <div
          v-if="!!multiStepPreRegisterTitle"
          :class="$style.title"
        >
          {{ multiStepPreRegisterTitle }}
        </div>

        <div
          v-if="!!multiStepPreRegisterSubtitle"
          :class="$style.subtitle"
        >
          {{ multiStepPreRegisterSubtitle }}
        </div>

        <template v-if="isSocialAuthEnabled && socialAuthFirst">
          <AuthSocialAuth
            buttons-only
            with-titles
          />

          <div :class="$style.orDivider">
            {{ $t('general.or') }}
          </div>
        </template>

        <button
          :class="$style.registerButton"
          @click="handleEmailRegister"
        >
          <NuxtIcon name="user/user_circle" />
          {{ $t('auth.register.register_with', { type: 'Email' }) }}
        </button>

        <template v-if="isSocialAuthEnabled && !socialAuthFirst">
          <div :class="$style.orDivider">
            {{ $t('general.or') }}
          </div>

          <AuthSocialAuth
            buttons-only
            with-titles
          />
        </template>

        <template v-if="activeRegisterWithPix">
          <div :class="$style.orDivider">
            {{ $t('general.or') }}
          </div>

          <button
            :class="$style.pixButton"
            @click="handlePixRegister"
          >
            <NuxtIcon name="payments/pix_icon" />
            {{ $t('auth.register.register_with', { type: 'PIX' }) }}
          </button>
        </template>

        <BaseModalFooter v-if="!isAuthV2FlowEnabled" />

        <AuthRegisterMultiStepWarningAlert />
      </div>
    </div>
  </section>
</template>

<style module lang="scss">
.boxCenter {
  @apply flex justify-center items-center m-auto;

  .registerContainer {
    @apply px-8 py-6 text-center max-w-md w-full;

    .title {
      @apply text-xl font-bold text-auth-texts mb-4;
    }

    .subtitle {
      @apply text-sm text-auth-texts/80 mb-6 -mt-2;
    }

    .registerButton {
      @apply bg-auth-button-bg text-auth-button-text py-3 px-4 rounded-lg w-full flex items-center justify-center gap-2 text-sm font-semibold;
    }

    .pixButton {
      @apply bg-transparent border border-auth-texts text-auth-texts py-3 px-4 rounded-lg w-full flex items-center justify-center gap-2 text-sm font-medium;
    }

    .orDivider {
      @apply flex items-center justify-center gap-4 text-xs font-semibold text-auth-texts/40 my-4 uppercase leading-3;

      &:before,
      &:after {
        content: '';
        @apply h-[1px] bg-auth-texts/10 flex-1 mt-0.5;
      }
    }
  }
}
</style>
